/* eslint-disable jsx-a11y/no-autofocus */
import React, { useMemo, useState } from 'react';
import classNames from 'classnames';

import { fontWeightEnum, Text, textVariantsEnum } from '@components/Text';

import styles from './Input.module.scss';

export const InputComponent = React.forwardRef(
  (
    {
      id,
      label,
      labelConfig = {},
      type = 'text',
      autoComplete = 'off',
      placeholder,
      className,
      style,
      hasError,
      errorMessage,
      successMessage,
      value,
      disabled,
      leftBlock,
      rightBlock,
      rightBlockClassName,
      leftBlockClassName,
      onChange = () => null,
      onFocus = () => null,
      onBlur = () => null,
      onClick = () => null,
      onKeyDown = () => null,
      alwaysFocused,
      onMouseUp,
      readOnly = false,
      min,
      max,
      step,
      list,
      inputLabelClassName,
      tooltip = {
        data: '',
        design: '',
      },
      smallErrorMessage,
      ...rest
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(!!alwaysFocused);

    const handleFocus = (e) => {
      if (disabled) return;

      setIsFocused(true);
      onFocus(e);
    };

    const handleBlur = (e) => {
      if (disabled) return;

      setIsFocused(false);
      onBlur(e);
    };
    const inputClass = classNames(
      styles.input,
      {
        [styles.input_focused]: isFocused,
        [styles.input_error]: !!errorMessage || hasError,
        [styles.input_disabled]: disabled,
      },
      className
    );
    const inputLabelClass = classNames(styles.inputLabel, inputLabelClassName);
    const rightBlockClass = classNames(styles.rightBlock, rightBlockClassName);
    const leftBlockClass = classNames(styles.leftBlock, leftBlockClassName);

    const leftRightBlockClickHandler = () => {
      if (alwaysFocused) {
        const input = document.querySelector(`#${id}`);
        input.focus();
      }
    };
    return (
      <div className={inputClass} style={style}>
        {!!label && (
          <Text
            variant={labelConfig.variant || textVariantsEnum.Text_sm}
            fontWeight={labelConfig.fontWeight || fontWeightEnum.Normal}
            color="gray-700"
            className={inputLabelClass}
          >
            {label}
          </Text>
        )}
        <div className={styles.inputContainer}>
          {leftBlock && (
            <div
              className={leftBlockClass}
              onClick={leftRightBlockClickHandler}
            >
              {leftBlock}
            </div>
          )}
          <section>
            <input
              key={`${id}_${!!alwaysFocused}`}
              autoFocus={!!alwaysFocused}
              autoComplete={autoComplete}
              placeholder={placeholder}
              onChange={!disabled ? onChange : undefined}
              onKeyDown={onKeyDown}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onWheel={(event) => event.target.blur()} //prevent default on wheel scroll in input field
              onClick={onClick}
              value={value}
              id={id}
              name={id}
              type={type}
              readOnly={readOnly || disabled}
              onMouseUp={onMouseUp}
              min={min}
              max={max}
              step={step}
              list={list}
              ref={ref}
              {...rest}
            />
            {list && (
              <datalist id="markers">
                <option value="70" label="70%"></option>
                <option value="100" label="100%"></option>
                <option value="130" label="130%"></option>
              </datalist>
            )}
          </section>
          {rightBlock && (
            <div
              className={rightBlockClass}
              onClick={leftRightBlockClickHandler}
            >
              {rightBlock}
            </div>
          )}
        </div>
        {!!errorMessage && (
          <Text
            variant={textVariantsEnum.Text_13}
            fontWeight={fontWeightEnum.Regular}
            color="error-500"
            className={styles.errorMessage}
          >
            {errorMessage}
          </Text>
        )}
        {!!successMessage && !errorMessage && (
          <Text
            variant={textVariantsEnum.Text_sm}
            color="success-700"
            className={styles.successMessage}
          >
            {successMessage}
          </Text>
        )}
      </div>
    );
  }
);

InputComponent.displayName = 'Input';
